/**
 * MeProfileCard Component
 *
 * Specialized card for the "Me" member that displays:
 * - User's actual profile picture (or initials fallback)
 * - User email from session
 * - "You" badge
 * Navigates to /edit-profile on press.
 */

import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useMeProfile } from '@/hooks/use-me-profile';
import { getContrastTextColor } from '@/lib/utils/color-contrast';
import type { FamilyMember } from '@/types/family-member';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type MeProfileCardProps = {
  member: FamilyMember;
  calendarCount: number;
  onPress: () => void;
};

export function MeProfileCard({
  member,
  calendarCount,
  onPress,
}: MeProfileCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { profilePictureUri, displayName, email } = useMeProfile();

  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = () => {
    scale.value = withTiming(0.97, { duration: 100 });
  };

  const handlePressOut = () => {
    scale.value = withTiming(1, { duration: 100 });
  };

  const calendarLabel = calendarCount === 1 ? '1 calendar' : `${calendarCount} calendars`;

  return (
    <AnimatedPressable
      style={[
        styles.container,
        Shadows.sm,
        {
          backgroundColor: palette.cardBackground,
          borderColor: member.color,
          borderWidth: 2,
        },
        animatedStyle,
      ]}
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      {/* Avatar with profile picture or placeholder icon */}
      {profilePictureUri ? (
        <Image
          source={{ uri: profilePictureUri }}
          style={styles.avatar}
        />
      ) : (
        <View style={[styles.avatarPlaceholder, { backgroundColor: member.color }]}>
          <Ionicons name="person" size={28} color="#FFFFFF" />
        </View>
      )}

      <View style={styles.content}>
        <View style={styles.nameRow}>
          <Text
            style={[styles.name, { color: palette.text, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {displayName}
          </Text>
          <View style={[styles.badge, { backgroundColor: member.color }]}>
            <Text style={[styles.badgeText, { color: getContrastTextColor(member.color), fontFamily: getBodyFont() }]}>You</Text>
          </View>
        </View>

        <View style={styles.details}>
          {email && (
            <Text
              style={[styles.detailText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {email}
            </Text>
          )}
          <Text
            style={[styles.detailText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {calendarLabel}
          </Text>
        </View>
      </View>

      <Ionicons name="chevron-forward" size={20} color={palette.textMuted} />
    </AnimatedPressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderRadius: BorderRadius.md,
    marginBottom: Spacing.md,
    gap: Spacing.md + 2, // 14px
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  avatarPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    gap: Spacing.xs,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  name: {
    fontSize: 17,
    fontWeight: '600',
  },
  badge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xxs,
    borderRadius: BorderRadius.sm + 2, // 10px
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '500',
  },
  details: {
    gap: Spacing.xxs,
  },
  detailText: {
    fontSize: 14,
  },
});
